import { AccessToken, OktaAuth } from '@okta/okta-auth-js';
import axios, { Axios } from 'axios';
import { isNil, isUndefined, toLower } from 'lodash';
import { Authentication } from '@gc-bridge/models';

export interface User {
  firstName: string;
  lastName: string;
  email: string;
  employeeId: number;
  groups: string[];
}

export class AuthService {
  private isAuthInProgress = false;
  oktaAuth: OktaAuth | undefined;
  personaSuffix = '';

  initOktaAuth(authConfig: Authentication) {
    if (this.oktaAuth) {
      return;
    }

    const {
      okta: { issuer, clientId },
      personaSuffix,
    } = authConfig;

    this.oktaAuth = new OktaAuth({
      issuer,
      clientId,
      redirectUri: window.location.origin + '/login/callback',
      scopes: ['openid', 'profile', 'offline_access'],
    });

    this.personaSuffix = personaSuffix ?? '';
  }

  async getProfile(): Promise<User | undefined> {
    const token = await this.ensureSession();

    if (!token || !this.oktaAuth) {
      return;
    }
    const { payload } = this.oktaAuth.token.decode(token);
    const {
      email = '',
      firstName = '',
      lastName = '',
      employeeId = NaN,
      groups = [],
    } = payload;

    return {
      email: toLower(email),
      firstName,
      lastName,
      employeeId: isNil(employeeId) ? NaN : Number(employeeId),
      groups,
    } as User;
  }

  async login() {
    if (
      !this.oktaAuth ||
      // Prevent multiple login popups if the session expires
      // and multiple requests are made simultaneously.
      this.isAuthInProgress ||
      // On 'login/callback' the loaded tokens are currently being stored,
      // no need to open the popup.
      window.location.pathname === '/login/callback'
    ) {
      return;
    }

    this.isAuthInProgress = true;

    const { tokens } = await this.oktaAuth.token.getWithPopup({});
    this.oktaAuth.tokenManager.setTokens(tokens);

    this.isAuthInProgress = false;

    return this.oktaAuth.getAccessToken();
  }

  async loginWithRedirect() {
    if (!this.oktaAuth) {
      return;
    }

    if (await this.oktaAuth.isAuthenticated()) {
      return;
    }

    // Being on 'login/callback' means we've already been redirected
    // and returned with tokens. The <LoginCallback> component will handle
    // storing the tokens and redirecting to the 'originalUri'.
    if (window.location.pathname !== '/login/callback') {
      await this.oktaAuth.signInWithRedirect({
        originalUri: window.location.href,
      });

      // We need this to stop further processing as the redirect is in progress.
      return { redirectInProgress: true };
    }
  }

  async logout() {
    if (!this.oktaAuth) {
      return;
    }

    return this.oktaAuth.signOut({
      postLogoutRedirectUri: window.location.origin + '/logout',
      clearTokensBeforeRedirect: true,
    });
  }

  async ensureSession(controller?: AbortController) {
    if (!this.oktaAuth) {
      return;
    }

    const tokens = this.oktaAuth.tokenManager.getTokensSync();
    let accessToken = tokens.accessToken;
    const refreshToken = tokens.refreshToken;

    if (
      isUndefined(accessToken) ||
      // if the refresh token has expired, we need to login again to retrieve a new one
      (refreshToken && this.oktaAuth.tokenManager.hasExpired(refreshToken))
    ) {
      /*
      Since the ongoing request will fail with 401,
      we cancel it to reduce network traffic.  
      */
      controller?.abort();
      const token = await this.login();
      return token;
    }

    /*
    The token manager, which is part of oktaAuth, renews the access token
    automatically when it expires. However, the way it is done is by silently
    making a request in the background for renewing the token. If we close the browser 
    and wait until the token expires, when opening the browser again the renew 
    operation may not have finished before the first request and the expired token 
    may still be in the storage, this is why we renew the token manually if it has expired:
    https://github.com/okta/okta-auth-js?tab=readme-ov-file#tokenmanagerhasexpiredtoken
    */
    if (accessToken && this.oktaAuth.tokenManager.hasExpired(accessToken)) {
      accessToken = (await this.oktaAuth.tokenManager.renew(
        'accessToken'
      )) as AccessToken;
    }

    return accessToken?.accessToken;
  }
  /* 
  The instance is needed for the specs, since there was race condition 
  related to the registration and ejecting of the interceptors.
  */
  registerTokenInterceptor(axiosInstance: Axios = axios) {
    axiosInstance.interceptors.request.use(
      async (config) => {
        const controller = new AbortController();
        config.signal = controller.signal;
        config.headers.Authorization = `Bearer ${await this.ensureSession(controller)}`;
        return config;
      },
      (error) => Promise.reject(error)
    );
  }
}

export const authService = new AuthService();

export const authProvider = {
  getProfile: () => authService.getProfile(),
  getToken: (controller?: AbortController) => authService.ensureSession(controller),
  logout: () => authService.logout(),
  getPersonaSuffix: () => authService.personaSuffix,
};
